import { Col, Row, Container, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import useUserApi from '@/shared/services/userApi';
import logger from '@/shared/utils/logger';
import isSafeUrl from '@/shared/utils/urlHelpers';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';
import userIcon from '@/assets/img/icons/svg/userIcon.svg';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';
import peopleIcon from '@/assets/img/icons/svg/peopleIcon.svg';
import SEO from '@/components/common/SEO';
import ProfileCompletionCard, {
    computeProfileCompletion,
} from '@/components/common/ProfileCompletionCard';

import ProfilePopUp from './ProfilePopUp';
import './ProfilePage.css';

export default function ProfilePage() {
    // const [notVerified, setVerified] = useState(true);

    const [popup, setPopup] = useState(false);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [error, setError] = useState(null);

    // access authenticated user, loading state, and log out function from auth context
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate(); // For navigation after logout

    const { fetchProfile } = useUserApi(); // get authenticated API methods

    const handleClose = () => setPopup(false);
    const handleShow = () => setPopup(true);

    useEffect(() => {
        let retryTimer = null;
        let cancelled = false;

        const fetchUserProfile = async (retryCount = 0) => {
            if (loading || !user) {
                setProfileLoading(false);
                return;
            }

            // Track whether this attempt scheduled a retry. The previous code
            // read `error?.response?.status` (a string state value), which is
            // never an object with `.response`, so the guard never matched and
            // the loading screen flickered off during 404 retries. Use a local
            // flag tied to this attempt instead.
            let retryScheduled = false;

            try {
                setProfileLoading(true);
                const profileData = await fetchProfile();
                if (cancelled) return;
                setProfile(profileData);
            } catch (err) {
                if (cancelled) return;
                logger.error('Failed to fetch profile: ', err);

                if (err.response?.status === 404 && retryCount < 3) {
                    // Retry fetching profile after a delay
                    retryScheduled = true;
                    retryTimer = setTimeout(
                        () => {
                            fetchUserProfile(retryCount + 1);
                        },
                        (retryCount + 1) * 2000
                    );
                    return;
                }

                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to load profile.'
                );
            } finally {
                // Keep the loading screen up while a retry is pending.
                if (!cancelled && !retryScheduled) {
                    setProfileLoading(false);
                }
            }
        };

        fetchUserProfile();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [fetchProfile, loading, user]);

    // handle profile update from ProfilePopUp modal
    const handleProfileUpdate = async (updatedUser) => {
        setProfile(updatedUser);

        // also refetch to ensure we have the latest data from server
        try {
            const refreshedProfile = await fetchProfile();
            setProfile(refreshedProfile);
        } catch (err) {
            logger.error('Failed to refresh profile after update:', err);
            // keep the updatedUser data even if refresh fails
        }
    };

    if (loading) {
        return (
            <main id="profile-page" className="page-container">
                <h3>Loading, please wait...</h3>
            </main>
        );
    }

    if (profileLoading) {
        return (
            <main id="profile-page" className="page-container">
                <h3>Loading profile, please wait...</h3>
            </main>
        );
    }

    if (!user) {
        return (
            <main id="profile-page" className="page-container">
                <h3>You must be logged in to view this page. Please log in.</h3>
            </main>
        );
    }

    if (error) {
        return (
            <main id="profile-page" className="page-container">
                <Container className="m-auto py-3">
                    <Alert variant="danger" className="text-center">
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p>{error}</p>
                        <Button
                            variant="outline-danger"
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </Button>
                    </Alert>
                </Container>
            </main>
        );
    }

    // get user name, with default
    const getUserName = (profile) => {
        if (profile?.name?.first && profile?.name?.last) {
            return `${profile.name.first} ${profile.name.last.charAt(0)}.`;
        }
        return 'No name provided.';
    };

    // get user email
    const getUserEmail = (profile) => {
        return profile?.email || user?.email || 'No email provided. ';
    };

    // get profile picture URL with fallback to default logo
    const getProfilePictureUrl = () => {
        return profile?.profilePicture || sustainabilityHubLogo;
    };

    // get user type with proper capitalization
    const getUserType = () => {
        if (profile?.userType) {
            return (
                profile.userType.charAt(0).toUpperCase() +
                profile.userType.slice(1)
            );
        }
        return null;
    };

    // get user bio
    const getUserBio = () => {
        return profile?.bio || null;
    };

    // get user organization
    const getUserOrganization = () => {
        return profile?.organization || null;
    };

    // get user interests array
    const getUserInterests = () => {
        return profile?.interests?.length > 0 ? profile.interests : null;
    };

    // get user locations array
    const getUserLocations = () => {
        return profile?.locations?.length > 0 ? profile.locations : null;
    };

    // get user external links array
    const getExternalLinks = () => {
        return profile?.externalLinks?.length > 0
            ? profile.externalLinks
            : null;
    };

    const handleLogout = async () => {
        try {
            await logout(); // Log the user out via Firebase
            navigate('/'); // Redirect to login page
        } catch (error) {
            logger.error('Logout failed:', error);
            // Optionally show a toast or message to the user
        }
    };

    const handleEdit = () => {
        navigate('/profile/edit');
    };

    return (
        <main
            id="profile-page"
            className="page-container d-flex justify-content-center align-items-start"
        >
            <SEO title="My Profile" />
            <Container className="m-auto py-3 py-lg-4 container">
                <h1>Account Profile</h1>
                {profile && (
                    <Row className="mb-3">
                        <Col lg={8} className="mx-auto">
                            <ProfileCompletionCard
                                profileCompletion={computeProfileCompletion(
                                    profile
                                )}
                                onStartTour={() => navigate('/dashboard')}
                            />
                        </Col>
                    </Row>
                )}
                <Container className="profile-container d-flex flex-column mx-auto w-100 py-3 py-lg-4 px-2 px-md-3">
                    <Row>
                        <Col md={6}>
                            <img
                                src={getProfilePictureUrl()}
                                alt={
                                    profile?.profilePicture
                                        ? 'User profile picture'
                                        : 'Default profile placeholder'
                                }
                                className="profile-pic"
                                onError={(e) => {
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.src = sustainabilityHubLogo;
                                }}
                            />
                            <div>
                                <Button
                                    className="update-profile-btn"
                                    type="button"
                                    onClick={handleShow}
                                >
                                    Update Profile Picture
                                </Button>

                                <ProfilePopUp
                                    show={popup}
                                    handleClose={handleClose}
                                    currentProfilePicture={
                                        profile?.profilePicture
                                    }
                                    onProfileUpdate={handleProfileUpdate}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="bg-white rounded-5 py-3">
                                <div className="py-2 px-3 border-bottom border-body-tertiary d-flex align-items-center gap-2">
                                    <img
                                        src={userIcon}
                                        alt="Username: "
                                        className="icon"
                                    />
                                    <p className="mb-0">
                                        <strong>Username</strong>
                                        <br />
                                        {profile?.username}
                                    </p>
                                </div>
                                <div className="py-2 px-3 border-bottom border-body-tertiary d-flex align-items-center gap-2">
                                    <img
                                        src={userIcon}
                                        alt="Name: "
                                        className="icon"
                                    />
                                    <p className="mb-0">
                                        <strong>Name</strong>
                                        <br />
                                        {getUserName(profile)}
                                    </p>
                                </div>
                                <div className="py-2 px-3 border-bottom border-body-tertiary d-flex align-items-center gap-2">
                                    <img
                                        src={emailIcon}
                                        alt="Email: "
                                        className="icon"
                                    />
                                    <p className="mb-0">
                                        <strong>Email</strong>
                                        <br />
                                        {getUserEmail(profile)}
                                    </p>
                                </div>
                                {getUserType() && (
                                    <div className="py-2 px-3 d-flex align-items-center gap-2">
                                        <img
                                            src={peopleIcon}
                                            alt="User Type: "
                                            className="icon"
                                        />
                                        <p className="mb-0">
                                            <strong>User Type</strong>
                                            <br />
                                            {getUserType()}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    {(getUserBio() ||
                        getUserOrganization() ||
                        getUserInterests() ||
                        getUserLocations()) && (
                        <div className="bg-primary rounded-5 py-3 mt-3">
                            {getUserBio() && (
                                <div className="py-2 px-3 border-bottom border-light">
                                    <p className="text-light mb-0">
                                        <strong>Bio</strong>
                                        <br />
                                        {getUserBio()}
                                    </p>
                                </div>
                            )}

                            {getUserOrganization() && (
                                <div className="py-2 px-3 border-bottom border-light">
                                    <p className="text-light mb-0">
                                        <strong>Organization</strong>
                                        <br />
                                        {getUserOrganization()}
                                    </p>
                                </div>
                            )}

                            {getUserInterests() && (
                                <div className="py-2 px-3 border-bottom border-light">
                                    <p className="text-light mb-0">
                                        <strong>Interests</strong>
                                        <br />
                                        {getUserInterests().join(', ')}
                                    </p>
                                </div>
                            )}

                            {getUserLocations() && (
                                <div className="py-2 px-3">
                                    <p className="text-light mb-0">
                                        <strong>Locations</strong>
                                        <br />
                                        {getUserLocations().join(', ')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {getExternalLinks() && (
                        <div className="user-info text-wrap">
                            <p className="mb-0">
                                <strong>External Links</strong>
                            </p>
                            <ul className="list-unstyled mb-0">
                                {getExternalLinks().map((link, i) => (
                                    <li key={`${link}-${i}`}>
                                        {isSafeUrl(link) ? (
                                            <a
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {link}
                                            </a>
                                        ) : (
                                            <span>{link}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Container>
                <div className="w-100 mt-3 d-flex flex-wrap justify-content-center justify-content-md-start gap-2">
                    <Button
                        type="button"
                        className="log-out-btn"
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to Dashboard
                    </Button>

                    <Button
                        type="button"
                        className="log-out-btn"
                        onClick={handleEdit}
                    >
                        Update Profile Info
                    </Button>

                    <Button
                        type="button"
                        className="log-out-btn"
                        onClick={handleLogout}
                    >
                        Log Out
                    </Button>
                </div>
            </Container>
        </main>
    );
}
